import type {
  BalconyConfig,
  DeckConfig,
  DoorConfig,
  FacadeSide,
  HouseBodyConfig,
  SlabHoleConfig,
  StairConfig,
  StoryConfig,
  TerraceConfig,
} from '@/model/schema';
import type { Rng } from './rng';
import { computeFacadeForbiddenSpans } from './openingsGenerator';

type Span = [number, number];

function intersectsSpan(spans: Span[], lo: number, hi: number): boolean {
  for (const [a, b] of spans) {
    if (hi > a && lo < b) return true;
  }
  return false;
}

/** Slide an offset left/right in 0.1m steps until the [offset, offset+width]
 *  range clears every forbidden span. Falls back to the original offset
 *  when no clean slot is reachable inside [rangeLo, rangeHi - width]. */
function nudgeOffset(
  spans: Span[],
  desired: number,
  width: number,
  rangeLo: number,
  rangeHi: number,
): number {
  const maxOffset = rangeHi - width;
  if (maxOffset < rangeLo) return desired;
  if (!intersectsSpan(spans, desired, desired + width)) return desired;
  for (let d = 0.1; d <= maxOffset - rangeLo; d += 0.1) {
    for (const sign of [-1, 1]) {
      const c = desired + sign * d;
      if (c < rangeLo || c > maxOffset) continue;
      if (!intersectsSpan(spans, c, c + width)) return c;
    }
  }
  return desired;
}

/**
 * Step 10c — extras: ground decks (patios) and upper-story balconies.
 *
 * Both attach to one of the four facades. Each runs after the openings
 * generator so:
 *   - Decks know where the front door is and avoid landing on top of it
 *     (or on top of an existing facade window run).
 *   - Decks can request their own back-door / side-door which we add to
 *     `story[0].doors` here, then drop any windows the new door collides
 *     with.
 *   - Balconies are placed on a non-ground story; they may also request
 *     a door, added to that story's doors.
 *
 * We keep the heuristics modest (one deck max, ~50% of houses get one;
 * one balcony max for multi-story houses, ~40%). The user can re-roll
 * to taste.
 */

export interface ExtrasResult {
  decks: DeckConfig[];
  /** Extra exterior doors keyed by story level — to be merged into stories. */
  extraDoorsByLevel: Map<number, DoorConfig[]>;
  /** Per-story balcony assignments (overrides story.balcony if present). */
  balconyByLevel: Map<number, BalconyConfig>;
  /** Window IDs to drop because an extras door now occupies that span. */
  droppedWindowIds: Set<string>;
  /** Terrace notches carved into the house. Each terrace cuts every
   *  story at `level` and above, plus the roof. Window IDs that overlap
   *  a terrace opening are moved to `droppedWindowIds` by the caller. */
  terraces: TerraceConfig[];
}

const DECK_PROBABILITY = 0.55;
const BALCONY_PROBABILITY = 0.45;
const TERRACE_PROBABILITY = 0.35;

export function generateExtras(
  rng: Rng,
  body: HouseBodyConfig,
  stories: StoryConfig[],
  stairs: StairConfig[] = [],
): ExtrasResult {
  const out: ExtrasResult = {
    decks: [],
    extraDoorsByLevel: new Map(),
    balconyByLevel: new Map(),
    droppedWindowIds: new Set(),
    terraces: [],
  };

  // Forbidden u-bands (per facade) at every story — partition contacts that
  // would slice through any opening placed there. Both deck and balcony
  // doors use these to avoid the user-reported "wall through balcony door"
  // case.
  const forbidden = computeFacadeForbiddenSpans(body, stories);

  if (rng.fork('deck-roll').chance(DECK_PROBABILITY)) {
    const deck = pickDeck(rng.fork('deck-pick'), body, stories[0]);
    if (deck) {
      let placedDoor = false;
      if (deck.hasDoorToHouse) {
        const door = makeDoorForFacade(body, deck.side, deck.offset, deck.length, 'rear', forbidden[deck.side]);
        if (door) {
          push(out.extraDoorsByLevel, 0, door);
          placedDoor = true;
          // Drop any ground-floor windows on the same facade that overlap
          // the new door span.
          for (const w of stories[0]?.windows ?? []) {
            if (w.side !== deck.side) continue;
            if (overlap(w.offset, w.offset + w.width, door.offset - 0.2, door.offset + door.width + 0.2)) {
              out.droppedWindowIds.add(w.id);
            }
          }
        }
      }
      // A patio without a door is still useful (you can walk onto it
      // from outside), so we always keep the deck — but keep the
      // hasDoorToHouse flag in sync with reality.
      const finalDeck = deck.hasDoorToHouse && !placedDoor
        ? { ...deck, hasDoorToHouse: false }
        : deck;
      out.decks.push(finalDeck);
    }
  }

  if (stories.length > 1 && rng.fork('balc-roll').chance(BALCONY_PROBABILITY)) {
    // Pick a non-ground story (favouring the topmost so the balcony has
    // open sky above it, which looks correct without roof modifications).
    const targetLevel = stories.length - 1;
    const story = stories[targetLevel]!;
    const balcony = pickBalcony(rng.fork('balc-pick'), body, story, forbidden);
    if (balcony) {
      // Unlike a deck, a balcony with no door is nonsensical — there's
      // no way to access it without dropping out of a window. If we
      // can't place a door, drop the balcony entirely. User reported
      // door-less balconies in both ground and upper variants.
      let placedDoor = false;
      if (balcony.hasDoorToHouse) {
        const door = makeDoorForFacade(body, balcony.side, balcony.offset, balcony.width, 'side', forbidden[balcony.side]);
        if (door) {
          push(out.extraDoorsByLevel, targetLevel, door);
          placedDoor = true;
          for (const w of story.windows) {
            if (w.side !== balcony.side) continue;
            if (overlap(w.offset, w.offset + w.width, door.offset - 0.2, door.offset + door.width + 0.2)) {
              out.droppedWindowIds.add(w.id);
            }
          }
        }
      }
      if (placedDoor) {
        out.balconyByLevel.set(targetLevel, balcony);
      }
    }
  }

  // Feed balconies back into the forbidden-span map so the terrace
  // picker can't land on (or straddle) the same facade run as a
  // balcony. Before this, the terrace and balcony could share a
  // facade and their railings / walls intersected near the corner
  // (user-reported bug with a balcony wall piercing a terrace wall).
  // We use a generous 0.6 m clearance so the corner-shared case also
  // stays clean.
  const BALC_TERRACE_CLEARANCE = 0.6;
  for (const balc of out.balconyByLevel.values()) {
    forbidden[balc.side].push([
      balc.offset - BALC_TERRACE_CLEARANCE,
      balc.offset + balc.width + BALC_TERRACE_CLEARANCE,
    ]);
  }

  // Terrace (rooftop / upper-story cut-in). Only generated for multi-story
  // houses and only at the TOP story for now — mid-story terraces would
  // need the wall/slab/roof builders to handle non-convex footprints on
  // every story in between, which we defer until this top-story variant
  // has shipped. The terrace's own door lives on the new interior wall
  // that faces the house rooms, so no facade door is emitted here.
  if (stories.length >= 2 && rng.fork('terrace-roll').chance(TERRACE_PROBABILITY)) {
    const terrace = pickTerrace(
      rng.fork('terrace-pick'),
      body,
      stories,
      stairs,
      forbidden,
    );
    if (terrace) {
      out.terraces.push(terrace);
      // Drop any exterior windows at the terrace's story that overlap
      // the terrace opening on EITHER exposed facade — the host facade
      // and its corner-sharing neighbour are both cut. Interior
      // partitions that slice through the notch are already excluded
      // by `pickTerrace` via the forbidden spans.
      const level = terrace.level;
      const story = stories[level];
      if (story) {
        const cuts = terraceFacadeCuts(body, terrace);
        const dropIfOverlap = (side: FacadeSide, lo: number, hi: number) => {
          for (const w of story.windows) {
            if (w.side !== side) continue;
            if (overlap(w.offset, w.offset + w.width, lo, hi)) {
              out.droppedWindowIds.add(w.id);
            }
          }
        };
        dropIfOverlap(cuts.hostSide, cuts.hostLo, cuts.hostHi);
        dropIfOverlap(cuts.adjSide, cuts.adjLo, cuts.adjHi);
      }
    }
  }

  return out;
}

/**
 * Resolve a corner terrace's adjacent-facade cut. Given the host side +
 * corner flag, returns:
 *   - the host-facade skip span (along u on that facade)
 *   - the adjacent facade and its skip span.
 *
 * `corner === 'origin'` means the notch is anchored at the host
 * facade's u=0 corner (which is also u=adjFacadeLen on the adjacent
 * facade — see buildWalls.ts for u conventions). `corner === 'far'`
 * anchors at u=facadeLen-width instead.
 */
export function terraceFacadeCuts(
  body: HouseBodyConfig,
  t: TerraceConfig,
): {
  hostSide: FacadeSide;
  hostLo: number;
  hostHi: number;
  adjSide: FacadeSide;
  adjLo: number;
  adjHi: number;
} {
  const W = body.footprintWidth;
  const D = body.footprintDepth;
  const hostLen = t.side === 'left' || t.side === 'right' ? D : W;
  const adjSide = adjacentSide(t.side, t.corner);
  const adjLen = adjSide === 'left' || adjSide === 'right' ? D : W;

  const hostLo = t.corner === 'origin' ? 0 : hostLen - t.width;
  const hostHi = hostLo + t.width;
  // Adjacent-facade span mirror: 'origin'-corner notches cover the FAR
  // end of the adjacent facade's u range; 'far'-corner notches cover
  // its NEAR (u=0) end.
  const adjLo = t.corner === 'origin' ? adjLen - t.depth : 0;
  const adjHi = adjLo + t.depth;
  return { hostSide: t.side, hostLo, hostHi, adjSide, adjLo, adjHi };
}

/** Which facade shares the corner selected by (side, corner). See
 *  buildWalls.ts for the origin-corner table:
 *    front origin = front-left, back origin = back-right,
 *    left origin = back-left, right origin = front-right.
 *  Each origin corner is shared with one specific neighbour facade;
 *  the 'far' corner is shared with the OTHER neighbour. */
export function adjacentSide(side: FacadeSide, corner: 'origin' | 'far'): FacadeSide {
  if (side === 'front') return corner === 'origin' ? 'left' : 'right';
  if (side === 'back') return corner === 'origin' ? 'right' : 'left';
  if (side === 'left') return corner === 'origin' ? 'back' : 'front';
  return corner === 'origin' ? 'front' : 'back';
}

/**
 * Produce the body-local AABB of a terrace notch: (xLo..xHi) × (zLo..zHi)
 * in the body's local coordinate system (origin at the body's lower-left
 * corner, +x toward right facade, +z toward back facade).
 */
export function terraceAabb(
  body: HouseBodyConfig,
  t: TerraceConfig,
): { xLo: number; xHi: number; zLo: number; zHi: number } {
  const W = body.footprintWidth;
  const D = body.footprintDepth;
  // Corner-anchored notch: derive the world (body-local) AABB from
  // `side` + `corner`. Offsets are no longer free — the notch always
  // starts at a body-plan corner and extends `width` along the host
  // facade and `depth` along the adjacent facade.
  const origin = t.corner === 'origin';
  switch (t.side) {
    case 'front':
      // front facade u=0 at front-left (x=0). Origin corner → x=0.
      return origin
        ? { xLo: 0, xHi: t.width, zLo: 0, zHi: t.depth }
        : { xLo: W - t.width, xHi: W, zLo: 0, zHi: t.depth };
    case 'back':
      // back facade u=0 at back-right (x=W). Origin corner → x=W.
      return origin
        ? { xLo: W - t.width, xHi: W, zLo: D - t.depth, zHi: D }
        : { xLo: 0, xHi: t.width, zLo: D - t.depth, zHi: D };
    case 'left':
      // left facade u=0 at back-left (z=D). Origin corner → z=D.
      return origin
        ? { xLo: 0, xHi: t.depth, zLo: D - t.width, zHi: D }
        : { xLo: 0, xHi: t.depth, zLo: 0, zHi: t.width };
    case 'right':
      // right facade u=0 at front-right (z=0). Origin corner → z=0.
      return origin
        ? { xLo: W - t.depth, xHi: W, zLo: 0, zHi: t.width }
        : { xLo: W - t.depth, xHi: W, zLo: D - t.width, zHi: D };
  }
}

function pickTerrace(
  rng: Rng,
  body: HouseBodyConfig,
  stories: StoryConfig[],
  stairs: StairConfig[],
  forbidden: Record<FacadeSide, [number, number][]>,
): TerraceConfig | null {
  const level = stories.length - 1;
  if (level < 1) return null;
  const story = stories[level];
  if (!story) return null;
  // Prefer back / sides — a front terrace would face the street which
  // is uncommon and collides with the entry walk. Each side has two
  // possible corners ('origin', 'far'), so the enumeration is 6
  // (sides × corners, minus front which we skip).
  const sidesShuffled: FacadeSide[] = rng.chance(0.5)
    ? ['back', 'right', 'left']
    : ['back', 'left', 'right'];
  const W = body.footprintWidth;
  const D = body.footprintDepth;
  for (const side of sidesShuffled) {
    const facadeLen = side === 'left' || side === 'right' ? D : W;
    const interiorDepth = side === 'left' || side === 'right' ? W : D;
    // Notch depth (cut inward from host facade): 1.4–3.2 m, clamped to
    // <= 40% of interior depth so the story can still host real rooms
    // in the remaining footprint.
    const maxDepth = Math.max(1.4, Math.min(3.2, interiorDepth * 0.4));
    if (maxDepth < 1.4) continue;
    const depth = rng.range(1.4, maxDepth);
    // Notch width (span along host facade starting at the corner). We
    // leave ≥ 1.2 m of remaining facade so the corner terrace never
    // reaches the opposite corner (which would split the story into
    // two disconnected halves).
    const maxWidth = Math.max(2.0, Math.min(4.5, facadeLen - 1.2));
    if (maxWidth < 2.0) continue;
    const width = rng.range(2.0, maxWidth);

    // Try both corners in random order. For each, check that the
    // host-facade span and its adjacent span are clear of partition
    // door bands and stair footprints.
    const cornerOrder: Array<'origin' | 'far'> = rng.chance(0.5)
      ? ['origin', 'far']
      : ['far', 'origin'];
    for (const corner of cornerOrder) {
      const tentative: TerraceConfig = {
        level,
        side,
        corner,
        width,
        depth,
        hasRailing: true,
        hasDoorToHouse: true,
        doorOffsetAlongInterior: Math.max(0.35, width / 2 - 0.5),
        doorWidth: 0.9,
      };
      const cuts = terraceFacadeCuts(body, tentative);
      if (intersectsSpan(forbidden[cuts.hostSide], cuts.hostLo, cuts.hostHi)) continue;
      if (intersectsSpan(forbidden[cuts.adjSide], cuts.adjLo, cuts.adjHi)) continue;
      if (collidesWithStair(body, tentative, stairs)) continue;
      return tentative;
    }
  }
  return null;
}

function collidesWithStair(
  body: HouseBodyConfig,
  terrace: TerraceConfig,
  stairs: StairConfig[],
): boolean {
  const aabb = terraceAabb(body, terrace);
  for (const s of stairs) {
    // Stair landings that emerge onto story L's floor consume floor
    // space at (x..x+width)*(z..z+depth). We treat both the rising flight
    // and its upper landing as a single footprint pad for conservatism.
    if (s.toLevel !== terrace.level && s.toLevel !== terrace.level + 0) continue;
    if (s.x + s.width <= aabb.xLo || s.x >= aabb.xHi) continue;
    if (s.z + s.depth <= aabb.zLo || s.z >= aabb.zHi) continue;
    return true;
  }
  return false;
}

/** Convert terrace notch AABBs into `slabHole` entries for any story
 *  strictly ABOVE the terrace's deck story. The deck story itself keeps
 *  its full floor slab (the slab IS the deck). Exported for the main
 *  generation pipeline to splice into each story's `slabHoles`. */
export function terraceSlabHolesForStory(
  body: HouseBodyConfig,
  terraces: TerraceConfig[],
  storyLevel: number,
): SlabHoleConfig[] {
  const out: SlabHoleConfig[] = [];
  for (const t of terraces) {
    if (storyLevel <= t.level) continue;
    const a = terraceAabb(body, t);
    out.push({
      x: a.xLo,
      z: a.zLo,
      width: a.xHi - a.xLo,
      depth: a.zHi - a.zLo,
    });
  }
  return out;
}

function pickDeck(rng: Rng, body: HouseBodyConfig, story: StoryConfig | undefined): DeckConfig | null {
  if (!story) return null;
  // Prefer back, then sides. Front decks are unusual and would clash
  // with the entry steps, so we never pick the front.
  const sides: FacadeSide[] = rng.chance(0.5)
    ? ['back', 'left', 'right']
    : ['back', 'right', 'left'];
  for (const side of sides) {
    const facadeLen = side === 'left' || side === 'right' ? body.footprintDepth : body.footprintWidth;
    const length = rng.range(2.4, Math.min(5.0, facadeLen - 1.4));
    if (length < 2.0) continue;
    const depth = rng.range(2.0, 3.5);
    const maxOffset = facadeLen - length - 0.5;
    if (maxOffset < 0.5) continue;
    const offset = rng.range(0.5, maxOffset);
    const hasStairs = body.plinthHeight > 0.05;
    return {
      side,
      offset,
      length,
      depth,
      hasStairsToGrade: hasStairs,
      hasDoorToHouse: rng.chance(0.85),
      hasRailing: rng.chance(0.4),
    };
  }
  return null;
}

function pickBalcony(
  rng: Rng,
  body: HouseBodyConfig,
  story: StoryConfig,
  forbidden: Record<FacadeSide, Span[]>,
): BalconyConfig | null {
  // Same side preference as decks (avoid front).
  const sides: FacadeSide[] = ['back', 'left', 'right', 'back'];
  for (let attempt = 0; attempt < 8; attempt++) {
    const side = sides[Math.floor(rng.next() * sides.length)] ?? 'back';
    const facadeLen = side === 'left' || side === 'right' ? body.footprintDepth : body.footprintWidth;
    const width = rng.range(1.6, Math.min(3.6, facadeLen - 1.0));
    if (width < 1.6) continue;
    const depth = rng.range(1.1, 2.0);
    const maxOffset = facadeLen - width - 0.4;
    if (maxOffset < 0.4) continue;
    const offset = rng.range(0.4, maxOffset);
    // Reject if a window straddles the candidate balcony span — the door
    // generator will drop it but we'd rather pick a clean spot first.
    let conflict = false;
    for (const w of story.windows) {
      if (w.side !== side) continue;
      if (overlap(w.offset, w.offset + w.width, offset, offset + width)) {
        conflict = true;
        break;
      }
    }
    if (conflict && attempt < 7) continue;
    // The future door's width is ~0.95m; centre it inside the balcony span
    // and refuse this candidate if every offset along the balcony hits a
    // partition forbidden band. That eliminates the user-reported case
    // where a partition wall slices through the balcony door.
    const doorW = 0.95;
    // Same EXT_CORNER_PAD as makeDoorForFacade: keep balcony doors well
    // clear of the corner so the surrounding pillar is substantial.
    const localLo = Math.max(0.6, offset);
    const localHi = Math.min(facadeLen - 0.6, offset + width);
    const center = offset + width / 2;
    const tryOffset = nudgeOffset(forbidden[side], center - doorW / 2, doorW, localLo, localHi);
    if (intersectsSpan(forbidden[side], tryOffset, tryOffset + doorW) && attempt < 7) continue;
    return { side, offset, width, depth, hasRailing: true, hasDoorToHouse: true };
  }
  return null;
}

function makeDoorForFacade(
  body: HouseBodyConfig,
  side: FacadeSide,
  spanOffset: number,
  spanLength: number,
  role: DoorConfig['role'],
  forbidden: Span[],
): DoorConfig | null {
  const facadeLen = side === 'left' || side === 'right' ? body.footprintDepth : body.footprintWidth;
  const doorWidth = Math.min(0.95, Math.max(0.8, spanLength * 0.35));
  const center = spanOffset + spanLength / 2;
  // Keep every extras-generated door at least `EXT_CORNER_PAD` meters from
  // either corner. 0.3 m (the old floor) left a paper-thin pillar between
  // the opening and the corner when combined with adjacent-facade windows,
  // which the user reported as an "incomplete corner" — hiking this to
  // 0.6 m matches the openings generator's typical minCornerOffset and
  // guarantees a substantial pier around every corner.
  const EXT_CORNER_PAD = 0.6;
  // Try to centre on the deck/balcony, then nudge to clear partition bands
  // and stay inside the deck/balcony's own footprint along the facade.
  const desired = Math.max(
    EXT_CORNER_PAD,
    Math.min(facadeLen - doorWidth - EXT_CORNER_PAD, center - doorWidth / 2),
  );
  // Restrict to the deck/balcony's own footprint along the facade so the
  // door always lands UNDER the deck or balcony slab (never to one side).
  const localLo = Math.max(EXT_CORNER_PAD, spanOffset);
  const localHi = Math.min(facadeLen - EXT_CORNER_PAD, spanOffset + spanLength);
  const offset = nudgeOffset(forbidden, desired, doorWidth, localLo, localHi);
  if (offset < 0 || offset + doorWidth > facadeLen) return null;
  // If we still overlap a forbidden band the deck/balcony spot is unusable.
  if (intersectsSpan(forbidden, offset, offset + doorWidth)) return null;
  return {
    id: `D-extras-${side}-${spanOffset.toFixed(2)}`,
    side,
    offset,
    width: doorWidth,
    height: 2.05,
    role,
  };
}

function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key) ?? [];
  list.push(value);
  map.set(key, list);
}

function overlap(a0: number, a1: number, b0: number, b1: number): boolean {
  return a1 > b0 && a0 < b1;
}
